import { supabase } from '../lib/supabase';
export const missionService = {
    // ============================================
    // MISSIONS CRUD
    // ============================================
    /**
     * Fetch all missions
     */
    async fetchMissions() {
        try {
            const { data, error } = await supabase
                .from('missions')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching missions:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch missions:', error);
            return [];
        }
    },
    /**
     * Get mission by ID
     */
    async getMission(id) {
        try {
            const { data, error } = await supabase
                .from('missions')
                .select('*')
                .eq('id', id)
                .single();
            if (error) {
                console.error('Error fetching mission:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to fetch mission:', error);
            return null;
        }
    },
    /**
     * Create a new mission
     */
    async createMission(mission) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { data, error } = await supabase
                .from('missions')
                .insert([{
                    ...mission,
                    created_by: user.id,
                    due_date: mission.due_date ? mission.due_date : null
                }])
                .select()
                .single();
            if (error) {
                console.error('Error creating mission:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to create mission:', error);
            return null;
        }
    },
    /**
     * Update a mission
     */
    async updateMission(id, updates) {
        try {
            // Convert empty date strings to null
            const cleanedUpdates = {
                ...updates,
                due_date: updates.due_date ? updates.due_date : null
            };
            const { data, error } = await supabase
                .from('missions')
                .update(cleanedUpdates)
                .eq('id', id)
                .select()
                .single();
            if (error) {
                console.error('Error updating mission:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to update mission:', error);
            return null;
        }
    },
    /**
     * Delete a mission
     */
    async deleteMission(id) {
        try {
            const { error } = await supabase
                .from('missions')
                .delete()
                .eq('id', id);
            if (error) {
                console.error('Error deleting mission:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete mission:', error);
            return false;
        }
    },
    // ============================================
    // MISSION-ORGANIZATIONS CRUD
    // ============================================
    /**
     * Link organization to mission
     */
    async linkOrganizationToMission(missionId, organizationId) {
        try {
            const { data, error } = await supabase
                .from('mission_organizations')
                .insert([{ mission_id: missionId, organization_id: organizationId }])
                .select()
                .single();
            if (error) {
                console.error('Error linking organization to mission:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to link organization to mission:', error);
            return null;
        }
    },
    /**
     * Get organizations for a mission
     */
    async getOrganizationsForMission(missionId) {
        try {
            const { data, error } = await supabase
                .from('mission_organizations')
                .select(`
          id,
          mission_id,
          organization_id,
          organizations (
            id,
            name,
            slug,
            description,
            avatar_url,
            type,
            plan,
            is_active,
            created_at
          )
        `)
                .eq('mission_id', missionId);
            if (error) {
                console.error('Error fetching organizations for mission:', error);
                throw error;
            }
            return data?.map((item) => item.organizations).filter(Boolean) || [];
        }
        catch (error) {
            console.error('Failed to fetch organizations for mission:', error);
            return [];
        }
    },
    // ============================================
    // GOALS CRUD
    // ============================================
    /**
     * Fetch all goals
     */
    async fetchGoals() {
        try {
            const { data, error } = await supabase
                .from('goals')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching goals:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch goals:', error);
            return [];
        }
    },
    /**
     * Get goals by organization ID
     */
    async getGoalsByOrganization(organizationId) {
        try {
            const { data, error } = await supabase
                .from('goals')
                .select('*')
                .eq('organization_id', organizationId)
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching goals by organization:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch goals by organization:', error);
            return [];
        }
    },
    /**
     * Get goals by mission ID
     */
    async getGoalsByMission(missionId) {
        try {
            const { data, error } = await supabase
                .from('goals')
                .select('*')
                .eq('mission_id', missionId)
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching goals by mission:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch goals by mission:', error);
            return [];
        }
    },
    /**
     * Create a new goal
     */
    async createGoal(goal) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { data, error } = await supabase
                .from('goals')
                .insert([{
                    ...goal,
                    created_by: user.id,
                    due_date: goal.due_date ? goal.due_date : null
                }])
                .select()
                .single();
            if (error) {
                console.error('Error creating goal:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to create goal:', error);
            return null;
        }
    },
    /**
     * Update a goal
     */
    async updateGoal(id, updates) {
        try {
            // Convert empty date strings to null
            const cleanedUpdates = {
                ...updates,
                due_date: updates.due_date ? updates.due_date : null
            };
            const { data, error } = await supabase
                .from('goals')
                .update(cleanedUpdates)
                .eq('id', id)
                .select()
                .single();
            if (error) {
                console.error('Error updating goal:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to update goal:', error);
            return null;
        }
    },
    /**
     * Delete a goal
     */
    async deleteGoal(id) {
        try {
            const { error } = await supabase
                .from('goals')
                .delete()
                .eq('id', id);
            if (error) {
                console.error('Error deleting goal:', error);
                throw error;
            }
            return true;
        }
        catch (error) {
            console.error('Failed to delete goal:', error);
            return false;
        }
    },
    // ============================================
    // PORTFOLIOS CRUD
    // ============================================
    /**
     * Fetch all portfolios
     */
    async fetchPortfolios() {
        try {
            const { data, error } = await supabase
                .from('portfolios')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching portfolios:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch portfolios:', error);
            return [];
        }
    },
    /**
     * Create a new portfolio
     */
    async createPortfolio(portfolio) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { data, error } = await supabase
                .from('portfolios')
                .insert([{ ...portfolio, created_by: user.id }])
                .select()
                .single();
            if (error) {
                console.error('Error creating portfolio:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to create portfolio:', error);
            return null;
        }
    },
    // ============================================
    // PROJECTS CRUD
    // ============================================
    /**
     * Fetch all projects
     */
    async fetchProjects() {
        try {
            const { data, error } = await supabase
                .from('projects')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching projects:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch projects:', error);
            return [];
        }
    },
    /**
     * Create a new project
     */
    async createProject(project) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { data, error } = await supabase
                .from('projects')
                .insert([{
                    ...project,
                    created_by: user.id,
                    due_date: project.due_date ? project.due_date : null,
                    start_date: project.start_date ? project.start_date : null
                }])
                .select()
                .single();
            if (error) {
                console.error('Error creating project:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to create project:', error);
            return null;
        }
    },
    // ============================================
    // TASKS CRUD
    // ============================================
    /**
     * Fetch all tasks
     */
    async fetchTasks() {
        try {
            const { data, error } = await supabase
                .from('board_items')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching tasks:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch tasks:', error);
            return [];
        }
    },
    /**
     * Create a new task
     */
    async createTask(task) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }
            const { data, error } = await supabase
                .from('board_items')
                .insert([{ ...task, created_by: user.id }])
                .select()
                .single();
            if (error) {
                console.error('Error creating task:', error);
                throw error;
            }
            return data;
        }
        catch (error) {
            console.error('Failed to create task:', error);
            return null;
        }
    },
    // ============================================
    // ORGANIZATION MEMBERS
    // ============================================
    /**
     * Fetch members of an organization for dropdown/selection
     */
    async fetchOrganizationMembers(organizationId) {
        try {
            const { data, error } = await supabase
                .from('organization_members')
                .select(`
          id,
          user_id,
          profiles:user_id (id, full_name, email)
        `)
                .eq('organization_id', organizationId);
            if (error) {
                console.error('Error fetching organization members:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('Failed to fetch organization members:', error);
            return [];
        }
    },
};
